import { logger } from "@/utils/logger";

import { useState, useRef, useCallback } from 'react';
import RecordRTC from 'recordrtc';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useDealerSession } from '@/contexts/DealerSessionContext';

interface UseVoiceRecordingOptions {
  onTranscriptionComplete?: (text: string) => void;
}

// Fetch the voice transcript prompt template from the database
const fetchVoiceTranscriptPrompt = async (): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('voice_transcript_prompt')
      .select('prompt_content')
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (error || !data?.prompt_content) {
      // Return default if not found
      return "This is a conversation between a customer and <$dealer_name$>. The dealer currently holds the following models in inventory to give you context and common terms: <$vehicle_models$>. The dealer's address is <$dealerAddress$>.";
    }
    
    return data.prompt_content;
  } catch (e) {
    console.error('Error fetching voice transcript prompt:', e);
    return "This is a conversation between a customer and <$dealer_name$>. The dealer currently holds the following models in inventory to give you context and common terms: <$vehicle_models$>. The dealer's address is <$dealerAddress$>.";
  }
};

// Fetch unique vehicle models from showroom-index.json
const fetchVehicleModels = async (domain: string): Promise<string[]> => {
  try {
    const url = `https://${domain}/js/json/chatboost/showroom/showroom-index.json`;
    logger.log('📦 Fetching vehicle models from:', url);
    const response = await fetch(url);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    logger.log('📦 Raw showroom data type:', Array.isArray(data) ? 'array' : typeof data, 'length:', Array.isArray(data) ? data.length : 'N/A');
    
    // Log first item structure for debugging
    if (Array.isArray(data) && data.length > 0) {
      logger.log('📦 First item keys:', Object.keys(data[0]));
    }
    
    const models = extractModelsFromShowroom(data);
    logger.log('📦 Extracted models:', models.length, 'items:', models.slice(0, 5).join(', '));
    return models;
  } catch (e) {
    console.error('📦 Error fetching vehicle models:', e);
    return [];
  }
};

// Fetch dealer address from dealer-info.json
const fetchDealerAddress = async (domain: string): Promise<string> => {
  try {
    const url = `https://${domain}/js/json/chatboost/dealer/dealer-info.json`;
    logger.log('📍 Fetching dealer address from:', url);
    const response = await fetch(url);
    if (!response.ok) {
      return '';
    }
    const data = await response.json();
    logger.log('📍 Raw dealer data:', JSON.stringify(data).substring(0, 200));
    
    // address is an object with { street, city, province, postal_code }
    if (data?.address && typeof data.address === 'object') {
      const { street, city, province, postal_code } = data.address;
      const parts = [street, city, province, postal_code].filter(Boolean);
      const formattedAddress = parts.join(', ');
      logger.log('📍 Formatted dealer address:', formattedAddress);
      return formattedAddress;
    }
    
    // Fallback for string address
    if (typeof data?.address === 'string') {
      return data.address;
    }
    
    logger.warn('📍 No address found in dealer-info.json');
    return '';
  } catch (e) {
    console.error('📍 Error fetching dealer address:', e);
    return '';
  }
};

// Extract unique models/trims from showroom data
const extractModelsFromShowroom = (data: any): string[] => {
  const models = new Set<string>();
  
  if (Array.isArray(data)) {
    data.forEach((item: any) => {
      const model = item.model || item.Model;
      
      // Add model name only
      if (model) models.add(model);
      
      // Extract trims and add model + trim combinations only
      const availableTrims = item['available trims'] || {};
      for (const trimName in availableTrims) {
        if (trimName && model) {
          models.add(`${model} ${trimName}`);
        }
      }
    });
  }
  
  return Array.from(models);
};

// Build the context prompt with variable replacement
const buildContextPrompt = async (
  dealerName: string,
  domain: string
): Promise<string> => {
  const [promptTemplate, vehicleModels, dealerAddress] = await Promise.all([
    fetchVoiceTranscriptPrompt(),
    fetchVehicleModels(domain),
    fetchDealerAddress(domain)
  ]);
  
  const modelsString = vehicleModels.length > 0 
    ? vehicleModels.slice(0, 50).join(', ') // Limit to 50 items to stay within token limit
    : 'various vehicle models';
  
  logger.log('📝 Prompt variables:', {
    dealerName,
    modelsCount: vehicleModels.length,
    dealerAddress: dealerAddress || '(empty)'
  });
  
  return promptTemplate
    .replace(/<\$dealer_name\$>/g, dealerName || 'the dealer')
    .replace(/<\$vehicle_models\$>/g, modelsString)
    .replace(/<\$dealerAddress\$>/g, dealerAddress || '');
};

export const useVoiceRecording = (options?: UseVoiceRecordingOptions) => {
  const { t } = useLanguage();
  const { sessionData } = useDealerSession();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription] = useState<string>('');
  const [error, setError] = useState<string>('');
  const recorderRef = useRef<RecordRTC | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cancelledRef = useRef(false);

  const startRecording = useCallback(async () => {
    try {
      setError('');
      cancelledRef.current = false; // Reset cancelled flag on new recording
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const recorder = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/wav',
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 16000,
        disableLogs: true,
        // Disable Web Workers to avoid CSP issues in development
        webWorker: false,
      });

      recorder.startRecording();
      recorderRef.current = recorder;
      setIsRecording(true);
    } catch (error) {
      // Error starting recording
      setError('Failed to start recording. Please check microphone permissions.');
    }
  }, []);

  const stopRecording = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!recorderRef.current) {
        const errorMsg = 'No recording found';
        setError(errorMsg);
        reject(new Error(errorMsg));
        return;
      }

      recorderRef.current.stopRecording(async () => {
        // Don't process if cancelled
        if (cancelledRef.current) {
          return;
        }
        try {
          setIsRecording(false);
          setIsTranscribing(true);
          setError('');

          const blob = recorderRef.current!.getBlob();
          
          // Stop all tracks
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
          }

          logger.log('🎤 Sending audio for transcription...');
          
          // Build context prompt for better transcription accuracy
          let contextPrompt = '';
          if (sessionData?.dealerName && sessionData?.domain) {
            try {
              logger.log('📝 Building context prompt with:', {
                dealerName: sessionData.dealerName,
                domain: sessionData.domain
              });
              
              contextPrompt = await buildContextPrompt(
                sessionData.dealerName,
                sessionData.domain
              );
              logger.log('📝 Full context prompt:', contextPrompt);
            } catch (e) {
              logger.warn('Could not build context prompt, continuing without it:', e);
            }
          } else {
            logger.warn('📝 Missing sessionData for context prompt:', {
              dealerName: sessionData?.dealerName,
              domain: sessionData?.domain
            });
          }
          
          // Send to edge function for transcription
          const formData = new FormData();
          formData.append('audio', blob, 'recording.wav');
          if (contextPrompt) {
            formData.append('prompt', contextPrompt);
          }
          if (sessionData?.domain) {
            formData.append('domain', sessionData.domain);
          }


          const { data, error: transcriptionError } = await supabase.functions.invoke('transcribe-audio', {
            body: formData,
          });

          if (transcriptionError || !data) {
            throw new Error(transcriptionError?.message || 'Transcription failed');
          }

          const transcribedText = data.text;
          logger.log('✅ Transcription complete:', transcribedText);
          
          setTranscription(transcribedText);
          setIsTranscribing(false);
          
          if (options?.onTranscriptionComplete) {
            options.onTranscriptionComplete(transcribedText);
          }
          
          resolve(transcribedText);
        } catch (error) {
          // Error transcribing audio
          const errorMsg = t('error.audio.transcription.failed');
          setError(errorMsg);
          setIsTranscribing(false);
          reject(new Error(errorMsg));
        }
      });
    });
  }, [options, t, sessionData]);

  const cancelRecording = useCallback(() => {
    // Set cancelled flag FIRST to prevent async callbacks from updating state
    cancelledRef.current = true;
    
    // Immediately reset state
    setIsRecording(false);
    setIsTranscribing(false);
    setTranscription('');
    setError('');
    
    // Then stop the recorder
    if (recorderRef.current) {
      recorderRef.current.stopRecording(() => {
        // Just stop without processing
      });
      recorderRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  return {
    isRecording,
    isTranscribing,
    transcription,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  };
};
